import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Truck, RotateCcw, Package, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Fetch featured products (seeding disabled to protect live data)
        const res = await axios.get(`${API}/products/featured`);
        setFeaturedProducts(res.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const trustFeatures = [
    { icon: Truck, title: 'Livrare Gratuită', desc: 'În toată România' },
    { icon: RotateCcw, title: 'Retur în 14 Zile', desc: 'Fără întrebări' },
    { icon: Package, title: 'Plata la Livrare', desc: 'Verifici coletul' },
    { icon: CheckCircle, title: 'Calitate Premium', desc: 'Garantat autentic' },
  ];

  const reviews = [
    { name: 'Alexandru M.', rating: 5, text: 'Calitate excelentă, livrare rapidă. A treia pereche de la DEPRO KICKS!' },
    { name: 'Maria P.', rating: 5, text: 'Cel mai bun magazin de sneakers din România. Yeezy-urile sunt perfecți.' },
    { name: 'Andrei C.', rating: 5, text: 'Plata la livrare m-a salvat. Am verificat coletul, totul a fost perfect.' },
  ];

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags for Homepage */}
      <Helmet>
        <title>DEPRO KICKS – Yeezy 350 V2 România | Magazin Sneakers Premium</title>
        <meta name="description" content="Descoperă cele mai populare modele Yeezy 350 V2 în România. Magazin online de sneakers premium cu modele Adidas Yeezy, Louboutin și McQueen. Livrare rapidă în România și retur garantat." />
        <meta name="keywords" content="yeezy, yeezy romania, yeezy 350 v2, adidas yeezy, sneakers romania, magazin yeezy, depro kicks, louboutin, alexander mcqueen" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1722970882096-b479417e3d24?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#0a0a0a]" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs tracking-[0.3em] text-[#c00000] uppercase mb-6 block">
              Stoc Limitat Disponibil
            </span>
            <h1 className="font-heading font-bold text-6xl md:text-8xl lg:text-9xl tracking-tight uppercase leading-none text-white mb-6" data-testid="hero-title">
              YEEZY 350 V2
            </h1>
            <p className="font-body text-lg md:text-xl text-white/70 mb-4 max-w-2xl mx-auto">
              Peste 30 de modele disponibile. Livrare gratuită în toată țara. Retur gratuit în 14 zile.
            </p>
            <p className="font-accent text-sm text-[#c00000] tracking-wider mb-10">
              PESTE 10.000 DE CLIENȚI MULȚUMIȚI
            </p>
            <Link to="/shop">
              <Button 
                className="bg-[#c00000] text-white hover:bg-[#a00000] rounded-none px-12 py-6 font-heading font-bold tracking-wider uppercase text-lg transition-all duration-300 hover:tracking-widest"
                data-testid="shop-now-btn"
              >
                CUMPĂRĂ ACUM <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Trust Features */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {trustFeatures.map((feature, idx) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 border border-white/10 flex items-center justify-center">
                <feature.icon className="w-7 h-7 text-[#c00000]" />
              </div>
              <h3 className="font-heading font-bold text-sm tracking-wider text-white mb-1">{feature.title}</h3>
              <p className="font-body text-xs text-white/50">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="font-body text-xs tracking-[0.3em] text-[#c00000] uppercase">Colecția</span>
          <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter uppercase text-white mt-2">
            PRODUSE RECOMANDATE
          </h2>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/5] bg-white/5" />
                <div className="h-4 bg-white/5 mt-4 w-2/3" />
                <div className="h-4 bg-white/5 mt-2 w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
            {featuredProducts.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        )}

        <div className="text-center mt-16">
          <Link to="/shop">
            <Button 
              variant="outline"
              className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black rounded-none px-12 py-6 font-heading font-bold tracking-wider uppercase transition-all duration-300"
              data-testid="view-all-btn"
            >
              VEZI TOATE PRODUSELE
            </Button>
          </Link>
        </div>
      </section>

      {/* Category Bento Grid */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link 
            to="/shop/yeezy-350-v2" 
            className="group relative h-[400px] md:h-[500px] lg:col-span-2 overflow-hidden bg-[#121212]"
            data-testid="category-yeezy-350"
          >
            <img 
              src="https://images.unsplash.com/photo-1622866654041-937b6e61b847?w=1200&q=80"
              alt="Yeezy 350 V2"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <span className="font-body text-xs tracking-[0.2em] text-[#c00000]">PESTE 30 MODELE</span>
              <h3 className="font-heading font-bold text-4xl md:text-5xl text-white mt-2 group-hover:text-[#c00000] transition-colors">
                YEEZY 350 V2
              </h3>
              <p className="font-body text-white/60 mt-2">De la 999 RON</p>
            </div>
          </Link>
          
          <Link 
            to="/shop/louboutin" 
            className="group relative h-[400px] md:h-[500px] overflow-hidden bg-[#121212]"
            data-testid="category-louboutin"
          >
            <img 
              src="https://images.unsplash.com/photo-1710668908762-7a380246d98e?w=800&q=80"
              alt="Louboutin"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <span className="font-body text-xs tracking-[0.2em] text-[#c00000]">LUX</span>
              <h3 className="font-heading font-bold text-3xl md:text-4xl text-white mt-2 group-hover:text-[#c00000] transition-colors">
                LOUBOUTIN
              </h3>
              <p className="font-body text-white/60 mt-2">De la 1550 RON</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Social Proof / Reviews */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#121212]">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="font-body text-xs tracking-[0.3em] text-[#c00000] uppercase">Ce Spun Clienții</span>
          <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter uppercase text-white mt-2">
            RECENZII CLIENȚI
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reviews.map((review, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-[#0a0a0a] border border-white/5 p-8"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#c00000] text-[#c00000]" />
                ))}
              </div>
              <p className="font-body text-white/70 mb-6 leading-relaxed">"{review.text}"</p>
              <p className="font-heading font-bold text-sm tracking-wider text-white">{review.name}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 md:px-12 lg:px-24 bg-[#0a0a0a] text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading font-bold text-5xl md:text-7xl tracking-tighter uppercase text-white mb-6">
            INTRĂ ÎN <span className="text-[#c00000]">PREMIUM</span>
          </h2>
          <p className="font-body text-lg text-white/60 max-w-xl mx-auto mb-10">
            Alătură-te celor peste 10.000 de clienți care aleg DEPRO KICKS pentru sneakers premium.
          </p>
          <Link to="/shop">
            <Button 
              className="bg-[#c00000] text-white hover:bg-[#a00000] rounded-none px-16 py-7 font-heading font-bold tracking-wider uppercase text-xl transition-all duration-300 hover:tracking-widest"
              data-testid="cta-shop-btn"
            >
              VEZI COLECȚIA
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
